import { useCallback, useEffect, useState } from 'react';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import LoginHeaderOperational from '../operational-model/LoginHeaderOperational';
import { API_URL } from '../../constant/API_Settings';
import {
  authHeaders,
  inboxGeneratedMeta,
  markReportNotificationRead,
  notifyReportDownloadsRefresh,
  REPORT_DOWNLOADS_REFRESH_EVENT,
} from '../../utils/reportFormat';
import './report-downloads.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'read', label: 'Read' },
  { id: 'queued', label: 'Generating' },
  { id: 'ready', label: 'Ready' },
  { id: 'failed', label: 'Failed' },
];

const EMPTY_QUEUE = {
  generating: 0,
  ready_unread: 0,
  failed_unread: 0,
  unread: 0,
  worker_jobs: 0,
  failed_jobs: 0,
};

function statusPill(item) {
  if (item.queue_status === 'queued') {
    return { className: 'rd-pill rd-pill-queued', text: 'Generating' };
  }
  if (item.queue_status === 'failed') {
    return { className: 'rd-pill rd-pill-failed', text: 'Failed' };
  }
  if (Number(item.status) === 1) {
    return { className: 'rd-pill rd-pill-unread', text: 'Unread' };
  }
  return { className: 'rd-pill rd-pill-read', text: 'Read' };
}

/**
 * Full PDF download inbox: every report, download links, read/unread filter, queue status.
 */
const ReportDownloads = (props) => {
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState(EMPTY_QUEUE);
  const [filter, setFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback((nextPage, nextFilter, nextSearch) => {
    setLoading(true);
    const params = new URLSearchParams({
      filter: nextFilter,
      search: nextSearch,
      page: String(nextPage),
      pagination_limit: '15',
    });

    fetch(`${API_URL}/report-downloads?${params.toString()}`, {
      method: 'get',
      headers: authHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        setItems(data.data || []);
        setQueue(data.queue || EMPTY_QUEUE);
        setPage(data.current_page || 1);
        setLastPage(data.last_page || 1);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, filter, search);
    const onRefresh = () => load(page, filter, search);
    window.addEventListener(REPORT_DOWNLOADS_REFRESH_EVENT, onRefresh);
    const timer = setInterval(onRefresh, 20000);
    return () => {
      window.removeEventListener(REPORT_DOWNLOADS_REFRESH_EVENT, onRefresh);
      clearInterval(timer);
    };
  }, [filter, page, search, load]);

  const openDownload = async (item) => {
    if (item.queue_status === 'queued') {
      return;
    }
    if (item.url && item.queue_status !== 'failed') {
      window.open(item.url, '_blank', 'noopener');
    }
    if (Number(item.status) === 1) {
      await markReportNotificationRead(item.id);
      notifyReportDownloadsRefresh();
      load(page, filter, search);
    }
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={8} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Report downloads" />
          <div className="container pb-3">
            <div className="rd-page">
              <section className="rd-hero">
                <div className="rd-hero-badge">Downloads</div>
                <h1>Your report files</h1>
                <p>
                  Every queued, ready, and failed PDF lives here. Unread files also show on the header download icon.
                  Opening a ready file marks it read and removes it from that list.
                </p>
              </section>

              <div className="rd-stats">
                <div className="rd-stat rd-stat-generating">
                  <strong>{queue.generating || 0}</strong>
                  <span>Generating now</span>
                </div>
                <div className="rd-stat rd-stat-ready">
                  <strong>{queue.ready_unread || 0}</strong>
                  <span>Ready unread</span>
                </div>
                <div className="rd-stat rd-stat-failed">
                  <strong>{queue.failed_unread || 0}</strong>
                  <span>Failed unread</span>
                </div>
                <div className="rd-stat rd-stat-queue">
                  <strong>{queue.worker_jobs || 0}</strong>
                  <span>In worker queue{queue.failed_jobs ? ` / ${queue.failed_jobs} failed jobs` : ''}</span>
                </div>
              </div>

              <div className="rd-toolbar">
                <div className="rd-filters">
                  {FILTERS.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className={`rd-filter ${filter === entry.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setFilter(entry.id);
                        setPage(1);
                      }}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
                <form
                  className="rd-search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setSearch(searchInput);
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search report name or note"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                  <button type="submit">Search</button>
                </form>
              </div>

              {loading && items.length === 0 ? (
                <div className="rd-empty">Loading reports…</div>
              ) : items.length === 0 ? (
                <div className="rd-empty">No reports in this view. Export a PDF from any report screen.</div>
              ) : (
                <div className="rd-card-list">
                  {items.map((item) => {
                    const pill = statusPill(item);
                    const title = item.report_name || item.title || 'Report';
                    const meta = inboxGeneratedMeta(item);
                    return (
                      <article
                        key={item.id}
                        className={`rd-card ${item.queue_status === 'queued' ? 'is-queued' : ''} ${item.queue_status === 'failed' ? 'is-failed' : ''} ${Number(item.status) === 1 ? 'is-unread' : ''}`}
                      >
                        <div>
                          <h2 className="rd-card-title">{title}</h2>
                          <p className="rd-card-note">{item.note}</p>
                          {(item.failure_reason || item.stuck_reason) ? (
                            <p className={`rd-reason ${item.failure_reason ? 'is-failed' : 'is-stuck'}`}>
                              <strong>Reason:</strong> {item.failure_reason || item.stuck_reason}
                            </p>
                          ) : null}
                          <span className={pill.className}>{pill.text}</span>
                          {item.queue_status === 'ready' ? (
                            <span className="rd-pill rd-pill-ready ms-2">PDF ready</span>
                          ) : null}
                          <div className="rd-card-meta">
                            {meta.when ? <span>{meta.when}</span> : null}
                            {meta.who ? <span>Generated by {meta.who}</span> : null}
                            {item.title && item.report_name ? <span>{item.title}</span> : null}
                          </div>
                        </div>
                        <div className="rd-card-actions">
                          {Number(item.status) === 1 && item.queue_status !== 'queued' ? (
                            <span className="inbox-unread-dot nt-card-dot" title="Unread" />
                          ) : null}
                          {item.queue_status === 'ready' && item.url ? (
                            <button type="button" className="rd-download-btn" onClick={() => openDownload(item)}>
                              Download PDF
                            </button>
                          ) : null}
                          {item.queue_status === 'queued' ? (
                            <button type="button" className="rd-ghost-btn" disabled>
                              Generating…
                            </button>
                          ) : null}
                          {item.queue_status === 'failed' && Number(item.status) === 1 ? (
                            <button
                              type="button"
                              className="rd-ghost-btn"
                              onClick={async () => {
                                await markReportNotificationRead(item.id);
                                notifyReportDownloadsRefresh();
                                load(page, filter, search);
                              }}
                            >
                              Mark read
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {lastPage > 1 ? (
                <div className="rd-pager">
                  <button type="button" className="rd-ghost-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  <span>Page {page} of {lastPage}</span>
                  <button type="button" className="rd-ghost-btn" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDownloads;
